// SupportTools.tsx - Support and system monitoring tools
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Settings, Refresh, Schedule } from "@mui/icons-material";
import AdminJson from "../../../utilities/AdminJson";
import { SupportApi, Configuration } from "api-client";

interface SystemInfo {
  python_version: string;
  django_version: string;
  database_engine: string;
  cache_backend: string;
  debug_mode: boolean;
  server_time: string;
  uptime: string;
}

export const SupportTools: React.FC = () => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(0); // 0 means disabled

  // Initialize API client
  const supportApi = new SupportApi(
    new Configuration({
      basePath: "", // Use same origin
      credentials: "same-origin",
    }),
  );

  useEffect(() => {
    fetchSystemInfo();
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshInterval === 0) return; // No auto-refresh

    const intervalId = setInterval(() => {
      handleManualRefresh();
    }, autoRefreshInterval * 1000); // Convert to milliseconds

    return () => clearInterval(intervalId);
  }, [autoRefreshInterval]);

  const fetchSystemInfo = async () => {
    try {
      const isInitialLoad = loading;
      if (!isInitialLoad) {
        setRefreshing(true);
      }
      setError(null);

      // Use the generated API client
      const response = await supportApi.supportSystemInfoRaw();
      const data = await response.raw.json();

      if (data) {
        setSystemInfo(data as SystemInfo);
        setLastUpdated(new Date());
      }

      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load system information",
      );
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = async () => {
    await fetchSystemInfo();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Support Tools
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Support Tools
        </Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchSystemInfo}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Support Tools
          </Typography>
          {lastUpdated && (
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Auto Refresh</InputLabel>
            <Select
              value={autoRefreshInterval}
              label="Auto Refresh"
              onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
              size="small"
            >
              <MenuItem value={0}>Disabled</MenuItem>
              <MenuItem value={10}>Every 10s</MenuItem>
              <MenuItem value={30}>Every 30s</MenuItem>
              <MenuItem value={60}>Every 1m</MenuItem>
              <MenuItem value={300}>Every 5m</MenuItem>
              <MenuItem value={900}>Every 15m</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={
              refreshing ? <CircularProgress size={16} /> : <Refresh />
            }
            onClick={handleManualRefresh}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? "Refreshing..." : "Refresh Data"}
          </Button>
        </Box>
      </Box>

      {/* Debug: Component State Data */}
      <AdminJson
        isDebug={true}
        componentName="SupportTools"
        data={{
          states: {
            systemInfo,
            loading,
            refreshing,
            error,
            lastUpdated,
            autoRefreshInterval,
          },
          actions: {
            fetchSystemInfo: "function",
            handleManualRefresh: "function",
          },
          metadata: {
            componentType: "Support Tools Dashboard",
            lastRendered: new Date().toISOString(),
          },
        }}
      />

      {/* System Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Schedule sx={{ mr: 1, color: "statusText.settled" }} />
                <Typography variant="h6">Uptime</Typography>
              </Box>
              <Typography variant="h6">{systemInfo?.uptime}</Typography>
              <Typography variant="body2" color="text.secondary">
                System running
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Settings
                  sx={{
                    mr: 1,
                    color: systemInfo?.debug_mode
                      ? "statusText.inFlight"
                      : "statusText.ready",
                  }}
                />
                <Typography variant="h6">Debug Mode</Typography>
              </Box>
              <Chip
                label={systemInfo?.debug_mode ? "ON" : "OFF"}
                color={systemInfo?.debug_mode ? "warning" : "success"}
                size="small"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Development mode
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* System Information */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
            <Typography variant="h6" gutterBottom>
              System Information
            </Typography>
            <TableContainer role="region" aria-label="System information" tabIndex={0}>
              <Table size="small" aria-label="System information" sx={{ minWidth: 400 }}>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Python Version</strong>
                    </TableCell>
                    <TableCell>{systemInfo?.python_version}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Django Version</strong>
                    </TableCell>
                    <TableCell>{systemInfo?.django_version}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Database</strong>
                    </TableCell>
                    <TableCell>{systemInfo?.database_engine}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Cache Backend</strong>
                    </TableCell>
                    <TableCell>{systemInfo?.cache_backend}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Server Time</strong>
                    </TableCell>
                    <TableCell>
                      {systemInfo?.server_time
                        ? new Date(systemInfo.server_time).toLocaleString()
                        : "Unknown"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
